import React, {lazy, Suspense, useEffect, useState} from 'react';
import {Modal, ModalContent, ModalContextBar, ModalFooter, ModalHeader} from "@/Components/Modal";
import DescriptionIcon from "@mui/icons-material/Description";
import {Button, Skeleton} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckIcon from "@mui/icons-material/Check";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import {graphql, PreloadedQuery, usePreloadedQuery, useQueryLoader} from "react-relay";
import {BookingInformationContent} from "@/Components/BookingInformation/BookingInformationContent";
import type {
    BookingInformationQuery as BookingInformationQueryType
} from './__generated__/BookingInformationQuery.graphql';
import {useSelector} from "react-redux";
import {getLocale, selectLocale} from "@/Redux/App/AppReducer";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";
import {UndoOutlined, VpnKey} from "@mui/icons-material";
import {useHistory} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "@/Redux/Redux";
import {useDisplayMessage} from "@/Utils/CustomHooks/useDisplayMessage";
import {FETCHING_DATA_START} from "@/Redux/Disposition/Constants";
import {logger} from "@/Logger";
import {BaseError} from "@/Utils/BaseError";
import DispoAcceptBookingMutation from "@/Domains/DispositionManagement/Mutations/DispoAcceptBookingMutation";
import DispoRejectBookingMutation from "@/Domains/DispositionManagement/Mutations/DispoRejectBookingMutation";
import UndoCheckOutMutation from "@/Domains/BookingManagement/Booking/Shared/Mutations/UndoCheckOutMutation";
import UndoCheckInMutation from "@/Domains/BookingManagement/Booking/Shared/Mutations/UndoCheckInMutation";
import PreviousContractNotCheckedInModal
    from "@/Domains/DispositionManagement/Components/Modals/PreviousContractNotCheckedInModal";

const EmailModalWrapper = lazy(
    () => import("@/Domains/CommunicationManagement/Containers/EmailModalWrapper")
);

export interface ModalProps {
    booking: any;
    checkDate: string;
    onClose: () => void;
}

const bookingModalQuery = graphql`
    query BookingInformationQuery(
        $bookingId: String!
        $checkDate: String!
        $lang: String!
        $prefixes: [String!]!
    ) {
        lastBookingStatus(bookingId: $bookingId, date: $checkDate) {
            bookingStatus
            lastBooking
        }
        mandant {
            modules {
                name
                enabled
            }
        }
        translationList(lang: $lang, prefixes: $prefixes) {
            translations {
                component
                text
            }
        }
        viewer {
            rights {
                right
            }
        }
    }
`;

export function BookingInformationModal({booking, checkDate, onClose}: ModalProps) {
    const [queryRef, loadQuery] = useQueryLoader<BookingInformationQueryType>(bookingModalQuery);
    const language = useSelector(selectLocale) ?? getLocale();
    useEffect(() => {
        loadQuery({
            bookingId: booking?.bookingId,
            checkDate,
            lang: language?.locale ?? language,
            prefixes: ["CRW6.general", "CRW6.bookingLine"]
        }, {fetchPolicy: "network-only"});
    }, [booking, checkDate, loadQuery]);

    return (
        <Modal open={true} onClose={onClose}>
            {queryRef ? (
                <Suspense fallback={<BookingInformationModalSkeleton onClose={onClose}/>}>
                    <BookingInformationModalBody queryRef={queryRef} onClose={onClose} booking={booking}/>
                </Suspense>
            ) : (
                <BookingInformationModalSkeleton onClose={onClose}/>
            )}
        </Modal>
    );
}

interface BookingModalBodyProps {
    queryRef: PreloadedQuery<BookingInformationQueryType>;
    onClose: () => void;
    booking: any
}

function BookingInformationModalBody({queryRef, onClose, booking}: BookingModalBodyProps) {
    const data = usePreloadedQuery(bookingModalQuery, queryRef);
    const {t} = useTranslation();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const {displayMessage} = useDisplayMessage();
    const {selectedStation, dispositionStartdate, dispositionEnddate} =
        useAppSelector((state) => state.disposition);

    const [wizardAcceptLoading, setWizardAcceptLoading] = useState(false);
    const [wizardRejectLoading, setWizardRejectLoading] = useState(false);
    const [acceptEmailModalOpen, setAcceptEmailModalOpen] = useState(false);
    const [rejectEmailModalOpen, setRejectEmailModalOpen] = useState(false);
    const [ciModalOpen, setCiModalOpen] = useState(false);

    const bookingId = booking?.bookingId;
    const status = booking?.status;
    const lastBooking = data.lastBookingStatus;

    // Disposition-Grid neu laden (Nebenwirkung aus dem alten BookingInfoModal übernommen)
    const refreshDisposition = () => {
        dispatch({
            type: FETCHING_DATA_START,
            payload: {
                startdate: dispositionStartdate,
                enddate: dispositionEnddate,
                selectedStation: selectedStation,
            },
        });
    };

    const handleShowBooking = () => {
        logger.log("show booking", {bookingId, bookingType: status});
        onClose();

        if (status === "reservation") {
            history.push(`/manager/reservation/${bookingId}/show`);
            return;
        } else if (status === "transfer_co" || status === "transfer_ci") {
            history.push(`/manager/transfer/${bookingId}/show`);
            return;
        } else if (status === "transfer_res") {
            history.push(`/manager/transfer-reservation/${bookingId}/show`);
            return;
        } else if (status === "contract_co" || status === "ci_made") {
            history.push(`/manager/contract/${bookingId}/show`);
            return;
        }

        throw new BaseError("Unknown booking type", {context: {bookingType: status}});
    };

    const handleConvert = () => {
        // Vorheriger Vertrag noch nicht eingecheckt -> Hinweis-Modal statt Navigation
        if (lastBooking?.lastBooking && lastBooking?.bookingStatus !== "ci_made") {
            setCiModalOpen(true);
            return;
        }

        onClose();
        if (status === "transfer_res") {
            history.push(`/manager/transfer-reservation/${bookingId}/r2m`);
        } else {
            history.push(`/manager/reservation/${bookingId}/r2m`);
        }
    };

    const handleCheckIn = () => {
        onClose();
        if (status === "transfer_co") {
            history.push(`/manager/transfer/${bookingId}/ci`);
        } else {
            history.push(`/manager/contract/${bookingId}/ci`);
        }
    };

    const handleUndoCheckout = () => {
        onClose();
        UndoCheckOutMutation(
            {bookingId: bookingId?.toString() ?? ""},
            (err) => {
                if (err) {
                    logger.log("undo checkout failed", {err});
                    displayMessage("error", t("messages.undoCheckoutFailure"));
                } else {
                    displayMessage("success", t("messages.undoCheckoutSuccess"));
                    refreshDisposition();
                }
            }
        );
    };

    const handleUndoCheckin = () => {
        onClose();
        UndoCheckInMutation(
            {bookingId: bookingId?.toString() ?? ""},
            (err) => {
                if (err) {
                    logger.log("undo checkin failed", {err});
                    displayMessage("error", t("messages.undoCheckinFailure"));
                } else {
                    displayMessage("success", t("messages.undoCheckinSuccess"));
                    refreshDisposition();
                }
            }
        );
    };

    const wizardAcceptReservation = () => {
        setWizardAcceptLoading(true);
        DispoAcceptBookingMutation(bookingId ?? "", (err) => {
            setWizardAcceptLoading(false);
            if (err) {
                logger.log("accept booking failed", {err});
                displayMessage("error", t("messages.accept_booking_failure"));
            } else {
                displayMessage("success", t("messages.accept_booking_success"));
            }
        });
    };

    const wizardRejectReservation = () => {
        setWizardRejectLoading(true);
        DispoRejectBookingMutation(bookingId ?? "", (err) => {
            setWizardRejectLoading(false);
            if (err) {
                logger.log("reject booking failed", {err});
                displayMessage("error", t("messages.reject_booking_failure"));
            } else {
                displayMessage("success", t("messages.reject_booking_success"));
            }
        });
    };

    const getHeaderText = (): string => {
        let text = "";
        if (status === "reservation") {
            text = t("general.reservation", "Reservierung");
        } else if (status === "transfer_co" || status === "transfer_ci") {
            text = t("general.transferDrive", "Transferfahrt");
        } else if (status === "contract_co") {
            text = t("general.rentalContract", "Mietvertrag");
        } else if (status === "ci_made") {
            text = t("general.rentalContract", "Mietvertrag");
        }

        return text;
    };

    const renderActions = () => {

        const isSpezRes = () => {
            const re = /\d+-\d+-[A-Za-z]+-\d/;
            if (booking?.reservationNumber?.match(re) !== null) return true;
            else return false;
        };

        const renderConfirmationAndDeny = () => {
            if (status !== "reservation") {
                return <></>
            }
            if (data.mandant?.modules?.find((module) => module?.name === "WIZARD")?.enabled) {
                if (isSpezRes()) {
                    return <></>
                }
                return <>
                    <Button color="success" variant="contained"
                            startIcon={<CheckIcon/>}
                            onClick={wizardAcceptReservation}
                            disabled={wizardAcceptLoading}>{t("bookingLine.confirmInWizard")}</Button>
                    <Button color="error" variant="outlined"
                            startIcon={<RemoveCircleIcon/>}
                            onClick={wizardRejectReservation}
                            disabled={wizardRejectLoading}>{t("bookingLine.declineInWizard")}</Button>
                </>
            }

            return <>
                <Button color="success" variant="contained" startIcon={<CheckIcon/>}
                        onClick={() => setAcceptEmailModalOpen(true)}>{t("bookingLine.confirm")}</Button>
                <Button color="error" variant="outlined"
                        startIcon={<RemoveCircleIcon/>}
                        onClick={() => setRejectEmailModalOpen(true)}>{t("bookingLine.decline")}</Button>
            </>
        }

        const renderCheckinAndCheckout = () => {
            if (status === "reservation" || status === "transfer_res") {
                return <Button color="secondary" variant="outlined"
                               startIcon={<SwapHorizIcon/>}
                               onClick={handleConvert}>{t("bookingLine.convert")}</Button>
            }
            if (status === "contract_co" || status === "transfer_co") {
                return <>
                    <Button
                        color="secondary"
                        variant="outlined"
                        startIcon={<VpnKey/>}
                        onClick={handleCheckIn}
                    >
                        {t("bookingLine.checkIn")}
                    </Button>
                    {data.viewer?.rights?.find(
                            (item) => item?.right === "VORG_SET_RES"
                        ) &&
                      <Button
                        color="secondary"
                        variant="outlined"
                        startIcon={<UndoOutlined/>}
                        onClick={handleUndoCheckout}
                      >
                          {t("bookingLine.undoCheckout")}
                      </Button>}
                </>
            }
            if ((status === "ci_made" ||
                    status === "transfer_ci") &&
                !booking?.isBilled &&
                data.viewer?.rights?.find(
                    (item) => item?.right === "VORG_SET_CO"
                )) {
                return <Button
                    color="secondary"
                    variant="outlined"
                    startIcon={<UndoOutlined/>}
                    onClick={handleUndoCheckin}
                >
                    {t("bookingLine.undoCheckin")}
                </Button>
            }
            return <></>
        }

        return <>
            <Button color="secondary" startIcon={<VisibilityIcon/>}
                    onClick={handleShowBooking}>{t("general.show")}</Button>
            {renderCheckinAndCheckout()}
            {renderConfirmationAndDeny()}
        </>
    }

    return (
        <>
            <ModalHeader
                icon={<DescriptionIcon/>}
                eyebrow={getHeaderText()}
                title={booking?.bookingId}
                onClose={onClose}
            />
            <ModalContextBar
                left={
                    <span>
                        {t("bookingLine.customer", "Kunde")} {booking?.customer?.customerId} ·{" "}
                        <b>{booking?.customer?.name1}</b>
                    </span>
                }
            />
            <ModalContent>
                <BookingInformationContent booking={booking}/>
            </ModalContent>
            <ModalFooter>
                {renderActions()}
            </ModalFooter>

            {acceptEmailModalOpen && (
                <Suspense fallback={<></>}>
                    <EmailModalWrapper
                        open={true}
                        handleClose={() => setAcceptEmailModalOpen(false)}
                        bookingId={bookingId}
                        type={"RB"}
                        openPrintMenu={true}
                    />
                </Suspense>
            )}
            {rejectEmailModalOpen && (
                <Suspense fallback={<></>}>
                    <EmailModalWrapper
                        open={true}
                        handleClose={() => setRejectEmailModalOpen(false)}
                        bookingId={bookingId}
                        type={"RA"}
                        openPrintMenu={false}
                    />
                </Suspense>
            )}
            {ciModalOpen && (
                <PreviousContractNotCheckedInModal
                    open={ciModalOpen}
                    handleClose={() => setCiModalOpen(false)}
                    bookingId={lastBooking?.lastBooking ?? ""}
                    type={lastBooking?.bookingStatus ?? ""}
                />
            )}
        </>
    );
}

function BookingInformationModalSkeleton({onClose}: { onClose: () => void }) {
    return (
        <>
            <ModalHeader
                icon={<DescriptionIcon/>}
                eyebrow={<Skeleton variant="text" width={90}/>}
                title={<Skeleton variant="text" width={120}/>}
                onClose={onClose}
            />
            <ModalContextBar
                left={<Skeleton variant="text" width={200}/>}
            />
            <ModalContent>
                {/* nutzt die loading-Skeletons, die in BookingContent schon eingebaut sind */}
                <BookingInformationContent booking={null} loading/>
            </ModalContent>
            <ModalFooter>
                
            </ModalFooter>
        </>
    );
}